import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Card, Avatar, Tag, Skeleton } from 'antd'
import { UserOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons'

const PageCard = React.memo(({ page }) => {
    const pageTypeText = useMemo(() => {
        if (page.typePage === "company" && page.typeCompany !== 'multi') {
            return "(Entreprise)";
        } else if (page.typeCompany === "multi") {
            return "(Groupe)";
        } else if (page.typePage === "partner") {
            return "(Partenaire)";
        }
        return "";
    }, [page.typePage, page.typeCompany]);

    const pageTypeIcon = useMemo(() => {
        if (page.typePage === "company" && page.typeCompany !== 'multi') {
            return <BankOutlined />;
        } else if (page.typeCompany === "multi") {
            return <TeamOutlined />;
        } else if (page.typePage === "partner") {
            return <UserOutlined />;
        }
        return <UserOutlined />;
    }, [page.typePage, page.typeCompany]);

    const cardLink = useMemo(() => 
        `/company/${page._id}?page=offers`,
        [page._id]
    );

    // Image par défaut si pas d'avatar
    const avatarUrl = page.avatar || '/src/images/icons/user.png';

    // Couleur du tag selon le type
    const getTagColor = () => {
        if (page.typePage === "company" && page.typeCompany !== 'multi') {
            return 'blue';
        } else if (page.typeCompany === "multi") {
            return 'purple';
        } else if (page.typePage === "partner") {
            return 'green';
        }
        return 'default';
    };

    return (
        <Link to={cardLink} className="block h-full group">
            <Card
                hoverable
                className="h-full transition-all duration-300 ease-in-out hover:shadow-lg hover:scale-[1.02] border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 group-hover:border-blue-300 dark:group-hover:border-blue-600"
                bodyStyle={{ 
                    padding: '16px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}
            >
                <div className="flex flex-col items-center text-center space-y-3 sm:space-y-4">
                    {/* Avatar avec fallback */}
                    <div className="relative">
                        <Avatar
                            size={{ xs: 48, sm: 56, md: 64, lg: 72, xl: 80, xxl: 88 }}
                            src={avatarUrl}
                            icon={<UserOutlined />}
                            className="border-2 border-slate-200 dark:border-slate-600 transition-colors duration-200 group-hover:border-blue-300 dark:group-hover:border-blue-600"
                            onError={() => {
                                // Fallback vers l'icône par défaut
                                return false;
                            }}
                        />
                    </div>

                    {/* Nom de la page */}
                    <div className="w-full">
                        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-slate-800 dark:text-slate-100 group-hover:text-slate-900 dark:group-hover:text-white transition-colors duration-200 line-clamp-2 leading-tight">
                            {page.name || 'Sans nom'}
                        </h3>
                    </div>

                    {/* Type de page avec icône */}
                    {pageTypeText && (
                        <div className="w-full">
                            <Tag 
                                color={getTagColor()}
                                icon={pageTypeIcon}
                                className="text-xs sm:text-sm px-2 py-1 rounded-full border-0 transition-all duration-200"
                            >
                                {pageTypeText.replace(/[()]/g, '')}
                            </Tag>
                        </div>
                    )}


                </div>
            </Card>
        </Link>
    )
});

// Composant de chargement pour les cartes
export const PageCardSkeleton = () => (
    <Card
        className="h-full border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 animate-pulse"
        styles={{ 
            padding: '16px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        }}
    >
        <div className="flex flex-col items-center text-center space-y-3 sm:space-y-4">
            <Skeleton.Avatar 
                size={{ xs: 48, sm: 56, md: 64, lg: 72, xl: 80, xxl: 88 }} 
                active 
            />
            <Skeleton.Input 
                size="small" 
                active 
                className="w-3/4"
            />
            <Skeleton.Input 
                size="small" 
                active 
                className="w-1/2"
            />
        </div>
    </Card>
);

PageCard.displayName = 'PageCard';

export default PageCard